import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Box, Typography, FormControl, Select, MenuItem, FormHelperText
} from '@mui/material';

import { AppContext } from "../context/AppContext";
import { financialService } from '../utils/api';
import CONFIG from "../utils/config";

const ugEducationOptions = [
  { value: '', label: 'Select Undergraduate (UG) Program', disabled: true },
  { value: 'Not Applicable', label: 'Not Applicable - No Cost' },
  { value: 'Reputed Indian', label: 'Reputed Indian College - ₹30 Lakhs' },
  { value: 'Premier Indian', label: 'Premier Indian College - ₹80 Lakhs' },
  { value: 'Premier Overseas', label: 'Premier Overseas College - ₹2.4 Cr' }
];

const pgEducationOptions = [
  { value: '', label: 'Select Postgraduate (PG) Program', disabled: true },
  { value: 'Not Applicable', label: 'Not Applicable - No Cost' },
  { value: 'Reputed Indian', label: 'Reputed Indian College - ₹20 Lakhs' },
  { value: 'Premier Indian', label: 'Premier Indian College - ₹50 Lakhs' },
  { value: 'Premier Overseas', label: 'Premier Overseas College - ₹1.5 Cr' }
];

// ---------------------------------------------------------------------------
// Sanitize payload — guarantees no NaN / undefined / empty string reaches backend
// ---------------------------------------------------------------------------
const buildChildPayload = (kidEdu) => {
  const safeFloat = (v) => { const n = parseFloat(v); return isNaN(n) ? 0.0 : n; };
  const safeStr   = (v) => (v && String(v).trim() ? String(v).trim() : 'Not Applicable');

  const ug_fees    = safeFloat(kidEdu.ug_fees);
  const ug_fees_FV = safeFloat(kidEdu.ug_fees_FV);
  const pg_fees    = safeFloat(kidEdu.pg_fees);
  const pg_fees_FV = safeFloat(kidEdu.pg_fees_FV);

  return {
    name:               safeStr(kidEdu.name),
    year_of_birth:      Number(kidEdu.yearOfBirth) || 0,
    age:                Number(kidEdu.Age)          || 0,
    ug_college:         safeStr(kidEdu.ug),
    ug_college_fees:    ug_fees,
    ug_college_fees_fv: Math.max(ug_fees_FV, ug_fees),
    pg_college:         safeStr(kidEdu.pg),
    pg_college_fees:    pg_fees,
    pg_college_fees_fv: Math.max(pg_fees_FV, pg_fees),
  };
};

// ---------------------------------------------------------------------------
const KidEducation = () => {
  const { state, actions } = useContext(AppContext);

  const [educationData, setEducationData] = useState(() =>
    state.kidsInfo.map((kid, index) => {
      const saved = state.kidsEducation?.[index] || {};
      return {
        ug: '',
        pg: '',
        ...saved,
        ug_fees: CONFIG.ug[saved.ug] || 0,
        ug_fees_FV: 0,
        pg_fees: CONFIG.pg[saved.pg] || 0,
        pg_fees_FV: 0,
      };
    })
  );

  // Sync from backend-loaded state (returning user)
  useEffect(() => {
    const updated = educationData.map((entry, idx) => {
      const Age = state.kidsInfo[idx]?.Age || 0;
      const ugValue = entry.ug || '';  // if fetched from backend
      const pgValue = entry.pg || '';

      const ugMatch = ugEducationOptions.find(opt => opt.value === ugValue);
      const pgMatch = pgEducationOptions.find(opt => opt.value === pgValue);

      return {
        ...entry,
        ug: ugMatch?.value || '',
        ug_fees: CONFIG.ug[ugMatch?.value] || 0,
        ug_fees_FV: ugMatch?.value
          ? CONFIG.ug[ugMatch.value] * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, CONFIG.DEFAULT_ug_AGE - Age)
          : 0,
        pg: pgMatch?.value || '',
        pg_fees: CONFIG.pg[pgMatch?.value] || 0,
        pg_fees_FV: pgMatch?.value
          ? CONFIG.pg[pgMatch.value] * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, CONFIG.DEFAULT_pg_AGE - Age)
          : 0,
      };
    });
    setEducationData(updated);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ref always holding LATEST kidsEducation + phoneNumber ─────────────────
  // Cleanup useEffect captures this ref so it reads current values at unmount,
  // not the stale values from when the component first mounted.
  const latestRef = useRef({
    kidsEducation: state.kidsEducation,
    phoneNumber:   state.phoneNumber,
  });

  useEffect(() => {
    latestRef.current = {
      kidsEducation: state.kidsEducation,
      phoneNumber:   state.phoneNumber,
    };
  }); // no deps — runs after every render to stay current

  // Fire submitChildrenInfo per kid when user navigates away (Next unmounts this step)
  useEffect(() => {
    return () => {
      const { kidsEducation, phoneNumber } = latestRef.current;

      kidsEducation.forEach(async (kidEdu) => {
        try {
          const payload = buildChildPayload(kidEdu);
          console.log('submitChildrenInfo payload:', JSON.stringify(payload)); // remove after confirming
          await financialService.submitChildrenInfo(phoneNumber, payload);
        } catch (err) {
          console.error('submitChildrenInfo failed for', kidEdu.name, err);
        }
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection handler ──────────────────────────────────────────────────────
  const handleChange = (index, type, value) => {
    const Age      = state.kidsInfo[index]?.Age || 0;
    const fees     = CONFIG[type][value] || 0;
    const ageConst = type === 'ug' ? CONFIG.DEFAULT_ug_AGE : CONFIG.DEFAULT_pg_AGE;
    const fees_FV  = fees
      ? fees * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, Math.max(0, ageConst - Age))
      : 0;

    const updated = [...educationData];
    updated[index][type] = value;
    Object.assign(updated[index], state.kidsInfo[index]);
    updated[index][type + "_fees"] = CONFIG[type][value];
    updated[index][type + "_fees_FV"] = Math.max(Number(updated[index][type + "_fees"]), Number(updated[index][type + "_fees"]) * Math.pow(1 + CONFIG.DEFAULT_INFLATION_RATE, CONFIG[`DEFAULT_${type}_AGE`] - updated[index]['Age']));
    actions.updateFormData("kidsEducation", updated);
    setEducationData(updated);
    actions.updateFormData('kidsEducation', updated);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2, mb: 4 }}>
      {state.kidsInfo.map((kid, kidIndex) => (
        <Box key={kidIndex}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            {`${kid.name.charAt(0).toUpperCase()}${kid.name.slice(1).toLowerCase()}'s Education`}
          </Typography>

          {/* UG */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <Select
                value={educationData[kidIndex].ug}
                onChange={(e) => handleChange(kidIndex, 'ug', e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2, '& .MuiSelect-select': { py: { xs: 1.5, sm: 2 } } }}
              >
                {ugEducationOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    sx={{ py: { xs: 1, sm: 1.5 } }}
                  >
                    {option.value === '' ? `Select Undergrad for ${kid.name}` : option.label}
                  </MenuItem>
                ))}
              </Select>
              {state.kidsInfo[kidIndex]?.Age > 18 && (
                <FormHelperText>Child age is more than 18 years</FormHelperText>
              )}
            </FormControl>
          </Box>

          {/* PG */}
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <Select
                value={educationData[kidIndex].pg}
                onChange={(e) => handleChange(kidIndex, 'pg', e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2, '& .MuiSelect-select': { py: { xs: 1.5, sm: 2 } } }}
              >
                {pgEducationOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    sx={{ py: { xs: 1, sm: 1.5 } }}
                  >
                    {option.value === '' ? `Select Postgrad for ${kid.name}` : option.label}
                  </MenuItem>
                ))}
              </Select>
              {state.kidsInfo[kidIndex]?.Age > 22 && (
                <FormHelperText>Child age is more than 22 years</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
      ))}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
        Note: We assume undergraduate education at age 18 and postgraduate education at age 22.
      </Typography>
    </Box>
  );
};

export default KidEducation;
